import React from 'react';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { acctTableHeader } from './typography';
import type { AccDocumentLine } from '../documents/useData';

const fmtAmt = (v: number | string | null | undefined) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return '';
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtRate = (v: number | string | null | undefined) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 3 });
};

const fmtText = (v?: string | null) => (v?.trim() ? v.trim() : '');

const fmtDate = (v?: string | null) => (v ? formatDateGlobal(v.slice(0, 10)) : '');

type MetaChip = { label: string; value: string };

const lineMetaChips = (line: AccDocumentLine, showLink: boolean): MetaChip[] => {
  const chips: MetaChip[] = [];
  if (showLink && fmtText(line.linkNumber)) chips.push({ label: 'Link', value: fmtText(line.linkNumber) });
  if (line.plPeriodFrom || line.plPeriodTo) {
    chips.push({
      label: 'P&L',
      value: [fmtDate(line.plPeriodFrom) || '…', fmtDate(line.plPeriodTo) || '…'].join(' → '),
    });
  }
  if (fmtText(line.dn)) chips.push({ label: 'DN', value: fmtText(line.dn) });
  if (fmtDate(line.dueDate)) chips.push({ label: 'Due', value: fmtDate(line.dueDate) });
  if (fmtText(line.notes)) chips.push({ label: 'Notes', value: fmtText(line.notes) });
  return chips;
};

type Props = {
  lines: AccDocumentLine[];
  embedded?: boolean;
};

const AmountCell: React.FC<{
  value: number | string | null | undefined;
  tone?: 'debit' | 'credit' | 'muted';
}> = ({ value, tone = 'muted' }) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const formatted = fmtAmt(value);
  if (!formatted) {
    return <span className={cn('text-sm tabular-nums', colors.textMuted)}>—</span>;
  }
  return (
    <span
      className={cn(
        'text-sm font-medium tabular-nums',
        tone === 'debit' && (isDark ? 'text-teal-300' : 'text-teal-700'),
        tone === 'credit' && (isDark ? 'text-amber-300' : 'text-amber-800'),
        tone === 'muted' && colors.text,
      )}
    >
      {formatted}
    </span>
  );
};

export const DocumentLinesView: React.FC<Props> = ({ lines, embedded = false }) => {
  const { colors } = useTheme();
  const sorted = [...lines].sort((a, b) => a.lineOrder - b.lineOrder || a.id - b.id);

  if (sorted.length === 0) {
    return (
      <div
        className={cn(
          'flex flex-col items-center justify-center px-6 py-12 text-center',
          !embedded && 'rounded-xl border',
          !embedded && colors.border,
          !embedded && colors.surface,
        )}
      >
        <p className={cn('text-sm font-medium', colors.text)}>No journal lines</p>
        <p className={cn('mt-1 text-xs', colors.textMuted)}>This journal has no posting lines yet.</p>
      </div>
    );
  }

  return (
    <div
      className={cn(
        embedded ? 'rounded-none border-0' : 'overflow-hidden rounded-xl border shadow-sm',
        !embedded && colors.border,
        !embedded && colors.surface,
      )}
    >
      <div className={cn('flex items-center justify-between gap-2 border-b px-4 py-2.5', colors.border, colors.headerBg)}>
        <div className="min-w-0">
          <h3 className={cn('text-sm font-semibold', colors.text)}>Journal lines</h3>
          <p className={cn('mt-0.5 text-xs', colors.textMuted)}>
            Debits and credits by company and account
          </p>
        </div>
        <span
          className={cn(
            'inline-flex shrink-0 items-center rounded-md px-2 py-0.5 text-xs font-medium tabular-nums',
            colors.surfaceHover,
            colors.text,
          )}
        >
          {sorted.length} line{sorted.length === 1 ? '' : 's'}
        </span>
      </div>

      <div className="dashboard-scrollbar overflow-x-auto">
        <table className={cn('w-full min-w-[56rem] table-fixed text-sm', colors.border)}>
          <thead className={cn('sticky top-0 z-10 border-b', colors.border, colors.headerBg)}>
            <tr>
              <th className={cn(acctTableHeader, 'w-12 px-2 py-2.5 text-center', colors.textSecondary)}>#</th>
              <th className={cn(acctTableHeader, 'w-[8.5rem] px-3 py-2.5 text-left', colors.textSecondary)}>Company</th>
              <th className={cn(acctTableHeader, 'px-3 py-2.5 text-left', colors.textSecondary)}>Account</th>
              <th className={cn(acctTableHeader, 'hidden w-[7.5rem] px-3 py-2.5 text-left lg:table-cell', colors.textSecondary)}>
                Narrative
              </th>
              <th className={cn(acctTableHeader, 'w-14 px-2 py-2.5 text-left', colors.textSecondary)}>CCY</th>
              <th className={cn(acctTableHeader, 'w-16 px-2 py-2.5 text-right', colors.textSecondary)}>Rate</th>
              <th className={cn(acctTableHeader, 'w-[6.25rem] px-3 py-2.5 text-right', colors.textSecondary)}>Debit</th>
              <th className={cn(acctTableHeader, 'w-[6.25rem] px-3 py-2.5 text-right', colors.textSecondary)}>Credit</th>
              <th className={cn(acctTableHeader, 'hidden w-[6.25rem] px-3 py-2.5 text-right xl:table-cell', colors.textSecondary)}>
                Dr USD
              </th>
              <th className={cn(acctTableHeader, 'hidden w-[6.25rem] px-3 py-2.5 text-right xl:table-cell', colors.textSecondary)}>
                Cr USD
              </th>
            </tr>
          </thead>
          <tbody className={cn('divide-y', colors.divide)}>
            {sorted.map((line, index) => {
              const showLink = !!line.account?.allowOpenItems;
              const chips = lineMetaChips(line, showLink);
              const companyLabel = line.company?.name ?? String(line.companyId);
              const accountLabel = line.account?.name ?? line.account?.code ?? String(line.accountId);
              const narrative = fmtText(line.narrative);
              const isDebit = Number(line.debitLocal) > 0;
              const isCredit = Number(line.creditLocal) > 0;

              return (
                <tr
                  key={line.id}
                  className={cn(
                    'group align-top transition-colors',
                    colors.interactiveHover,
                    isDebit && 'border-l-[3px] border-l-teal-500/70',
                    isCredit && 'border-l-[3px] border-l-amber-500/70',
                    !isDebit && !isCredit && 'border-l-[3px] border-l-transparent',
                  )}
                >
                  <td className="px-2 py-3 text-center">
                    <span
                      className={cn(
                        'inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-md px-1.5 text-[11px] font-semibold tabular-nums',
                        colors.textMuted,
                        colors.surfaceHover,
                      )}
                    >
                      {index + 1}
                    </span>
                  </td>

                  <td className="px-3 py-3">
                    <p className={cn('truncate text-sm font-medium', colors.text)} title={companyLabel}>
                      {companyLabel}
                    </p>
                  </td>

                  <td className="px-3 py-3">
                    <div className="min-w-0">
                      <p className={cn('truncate text-sm font-medium', colors.text)} title={accountLabel}>
                        {accountLabel}
                      </p>
                      {narrative && (
                        <p className={cn('mt-0.5 truncate text-xs lg:hidden', colors.textMuted)} title={narrative}>
                          {narrative}
                        </p>
                      )}
                      {chips.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {chips.map((chip) => (
                            <span
                              key={`${chip.label}-${chip.value}`}
                              title={`${chip.label}: ${chip.value}`}
                              className={cn(
                                'inline-flex max-w-full items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px]',
                                colors.border,
                                colors.headerBg,
                              )}
                            >
                              <span className={cn('shrink-0 font-semibold uppercase tracking-wide', colors.textMuted)}>
                                {chip.label}
                              </span>
                              <span className={cn('truncate', colors.text)}>{chip.value}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </td>

                  <td className="hidden px-3 py-3 lg:table-cell">
                    <p className={cn('line-clamp-2 text-sm', colors.textMuted)} title={narrative || undefined}>
                      {narrative || '—'}
                    </p>
                  </td>

                  <td className={cn('px-2 py-3 text-sm tabular-nums', colors.text)}>
                    {line.currency?.code ?? '—'}
                  </td>

                  <td className={cn('px-2 py-3 text-right text-sm tabular-nums', colors.textMuted)}>
                    {fmtRate(line.rate)}
                  </td>

                  <td className="px-3 py-3 text-right">
                    <AmountCell value={line.debitLocal} tone="debit" />
                  </td>
                  <td className="px-3 py-3 text-right">
                    <AmountCell value={line.creditLocal} tone="credit" />
                  </td>
                  <td className="hidden px-3 py-3 text-right xl:table-cell">
                    <AmountCell value={line.debitUSD} tone="muted" />
                  </td>
                  <td className="hidden px-3 py-3 text-right xl:table-cell">
                    <AmountCell value={line.creditUSD} tone="muted" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
